import { useApolloClient } from "@apollo/client";
import { getDataOrNull } from "@msys/common";
import { Grid } from "@mui/material";
import React from "react";
import { useLocation, useNavigate, useParams } from "react-router-dom";
import { TemplateSearchFiltersInput } from "../../../../clients/graphqlTypes.js";
import { RestrictedByOrganisationPermissionWithDebug } from "../../../auth/RestrictedByOrganisationPermission.js";
import {
  BreadcrumbItem,
  Page,
  PageTopbarItem,
} from "../../../commons/layout/Page.js";
import { PageContainer } from "../../../commons/layout/PageContainer.js";
import { CreateQuoteTemplateButton } from "../../../features/templates/quote/buttons/CreateTemplateQuoteButton.js";
import {
  DEFAULT_FILTERS,
  DEFAULT_SORTING,
  TemplatesDisplay,
} from "../../../features/templates/quote/TemplatesDisplay.js";
import { TemplateTypeOverviewHeaderBox } from "../../../features/templateTypes/TemplateTypeOverviewHeaderBox.js";
import { TemplateTypeOverviewPropertyBox } from "../../../features/templateTypes/TemplateTypeOverviewPropertyBox.js";
import { buildDocPath } from "../../../utils.js";
import { useTemplateQuoteTemplateTypeQuery } from "./TemplateQuoteTemplateType.generated.js";
import { useTemplatesQueryVariablesInUrl } from "../../../features/templates/quote/useTemplatesQueryVariables.js";
import { searchQuotesSortingToQuoteTemplatesSorting } from "../../../features/templates/helpers.js";

interface Props {
  docId: string;
  submenuItems: PageTopbarItem[];
  prefixBreadcrumbs: BreadcrumbItem[];
  pageSubHeader: React.ReactElement | null;
  pathToDocList: string;
}

export function TemplateQuoteTemplateType({
  docId,
  submenuItems,
  prefixBreadcrumbs,
  pageSubHeader,
  pathToDocList,
}: Props) {
  const { templateTypeId } = useParams();
  if (!templateTypeId) throw new Error("Template type id missing");

  const location = useLocation();

  const client = useApolloClient();
  const templateTypeQuery = useTemplateQuoteTemplateTypeQuery({
    client,
    variables: { id: templateTypeId },
  });
  const templateType = getDataOrNull(templateTypeQuery.data?.templateType);

  return (
    <Page
      title={templateType?.title ?? ""}
      submenuItems={submenuItems}
      breadcrumbs={prefixBreadcrumbs.concat({
        title: templateType?.title ?? "",
        to: location.pathname,
      })}
      subHeader={pageSubHeader}
      action={[
        <RestrictedByOrganisationPermissionWithDebug permission="MANAGE_TEMPLATE">
          <CreateQuoteTemplateButton
            pathToDocList={pathToDocList}
            templateTypeId={templateTypeId}
          />
        </RestrictedByOrganisationPermissionWithDebug>,
      ]}
      headerType="template"
    >
      {templateType && (
        <PageContainer>
          <Grid container spacing={2}>
            <Grid
              size={{
                xs: 12,
                md: 6,
                xl: 3,
              }}
            >
              <TemplateTypeOverviewHeaderBox templateType={templateType} />
            </Grid>
            <Grid
              size={{
                xs: 12,
                md: 6,
                xl: 3,
              }}
            >
              <TemplateTypeOverviewPropertyBox templateType={templateType} />
            </Grid>
            <Grid size={12}>
              <TemplatesList
                templateTypeId={templateTypeId}
                pathToDocList={pathToDocList}
              />
            </Grid>
          </Grid>
        </PageContainer>
      )}
    </Page>
  );
}

const TemplatesList = ({
  templateTypeId,
  pathToDocList,
}: {
  templateTypeId: string;
  pathToDocList: string;
}) => {
  const navigate = useNavigate();

  const {
    paginationModel,
    setPaginationModel,
    filters,
    setFilters,
    resetFilters,
    sorting,
    setSorting,
    queryBaseVariables,
    searchTerm,
    setSearchTerm,
  } = useTemplatesQueryVariablesInUrl<TemplateSearchFiltersInput>({
    initialFilters: DEFAULT_FILTERS,
    initialSorting: searchQuotesSortingToQuoteTemplatesSorting(DEFAULT_SORTING),
    initialSearchTerm: "",
    searchUrlParam: "templatesDisplaySearch",
    pageUrlParam: "templatesDisplayPage",
    perPageUrlParam: "templatesDisplayPageSize",
    filtersUrlParam: "templatesDisplayFilters",
    sortingsUrlParam: "templatesDisplaySortings",
  });

  const fixedFilters = React.useMemo(
    (): TemplateSearchFiltersInput => ({
      templateTypeIds: [templateTypeId],
      sources: ["mine_unpublished", "subscribed"],
    }),
    [templateTypeId]
  );

  return (
    <TemplatesDisplay
      onTemplateClick={template =>
        navigate(
          buildDocPath(
            `${pathToDocList}/${template.id}`,
            template.resolvedAsReadModelVersionNumber ?? null
          )
        )
      }
      fixedFilters={fixedFilters}
      paginationModel={paginationModel}
      setPaginationModel={setPaginationModel}
      filters={filters}
      setFilters={setFilters}
      resetFilters={resetFilters}
      sorting={sorting}
      setSorting={setSorting}
      queryBaseVariables={queryBaseVariables}
      searchTerm={searchTerm}
      setSearchTerm={setSearchTerm}
    />
  );
};
